import type { MakeADT } from 'ts-adt/MakeADT'

import type { DTCheckbox, DTDate, DTNumber, DTSelect, TypeInstanceMap } from './data'
import type {
    BoolValue,
    DateValue,
    FieldId,
    FieldType,
    FileValue,
    FormulaValue,
    NumberValue,
    RichTextValue,
    StringArrayValue,
    TextValue
} from './field'
import type {
    AggregateProtocol,
    FilterableProtocol,
    HighlightingProtocol,
    JoinProtocol,
    PaginationProtocol,
    PromptProtocol,
    SortableProtocol,
    TablePropsProtocol
} from './general'
import type { RecordLikeProtocol } from './record'

export type ViewId = string

export type ViewType = 'table' | 'advancedTable' | 'list' | 'gallery' | 'kanban' | 'calendar' | 'custom'

export type RelatedViewType = 'relatedTable' | 'relatedAdvancedTable' | 'relatedList' | 'relatedGallery'

export type ExtraViewFieldType = 'formula'

export enum SelectedMode {
    'ALL' = 'ALL',
    'SINGLE' = 'SINGLE',
    'CURRENT_PAGE' = 'CURRENT_PAGE'
}
// export interface TreeRecordWithChildIds {
//     record: RecordLikeProtocol
//     count: number
//     size: number
//     childRecords: RecordLikeProtocol[]
// }

export interface TreeRecordWithChildIds {
    record: RecordLikeProtocol
    count: number
    size: number
    childIds: string[]
}

export type RecordPoint = {
    id: string
    disabled?: boolean
}

export type ViewCustomStatisticValues = Record<string, Record<string, number | null>>

export type ViewIndependentData = {
    dsId: string
    search?: string
    currentPage: number
    rowTotal?: number
    recordPoints: RecordPoint[]
    /** 自定义视图统计结果，按当前视图的 recordId 存，不写入共享 recordPool */
    customStatistic?: ViewCustomStatisticValues
}

export interface DataViewProtocol {
    viewOptions: TablePropsProtocol &
        PaginationProtocol &
        SortableProtocol &
        FilterableProtocol &
        HighlightingProtocol &
        PromptProtocol &
        JoinProtocol &
        AggregateProtocol
}

export interface ViewTypeProtocol {
    viewType: ViewType
}

export type RichTextViewFieldMode = 'TEXT' | 'PERSON'

export type NumberViewFieldMode = 'NUMBER' | 'PERCENTAGE'

export interface ExtraBaseViewField {
    fieldId: string
    title: string
}

export type GroupRecordCount = {
    value: string
    count: number | undefined
}

export type GroupTab = {
    label: string
    value: string
    count: number | undefined
}

export type TableColumnWidth = Record<string, number>

export interface ViewFieldProtocol {
    title: string
    fieldId: string
    type: FieldType
    innerType?: TypeInstanceMap
    dsId?: string
    dsName?: string

    // 连接表中字段
    join?: {
        joinDsId: string
        joinFieldId: string
    }
}

export interface ViewFieldProps extends ViewFieldProtocol {
    width?: number
    required?: boolean
    visible: boolean
}

export interface TextViewField extends ViewFieldProps {}

export interface DateViewField extends ViewFieldProps {
    date: DTDate
}

export interface NumberViewField extends ViewFieldProps {
    number: DTNumber
}

export interface CheckboxViewField extends ViewFieldProps {
    checkbox: DTCheckbox
}

export type SelectViewField = ViewFieldProps & {
    select: DTSelect
}

export interface BaseViewField extends ViewFieldProps {}

export type ViewField = MakeADT<
    'type',
    {
        id: BaseViewField
        text: TextViewField
        url: BaseViewField
        phoneNumber: BaseViewField
        email: BaseViewField
        file: BaseViewField
        video: BaseViewField
        person: BaseViewField
        createdTime: DateViewField
        updatedTime: DateViewField
        number: NumberViewField
        select: SelectViewField
        date: DateViewField
        formula: BaseViewField
        notes: BaseViewField
        role: BaseViewField
        department: BaseViewField
        checkbox: CheckboxViewField
    }
>

export type ViewFieldADTValue = MakeADT<
    'type',
    {
        id: BaseViewField & { value: TextValue }
        text: TextViewField & { value: TextValue }
        url: BaseViewField & { value: TextValue }
        phoneNumber: BaseViewField & { value: TextValue }
        email: BaseViewField & { value: TextValue }
        file: BaseViewField & { value: FileValue }
        video: BaseViewField & { value: FileValue }
        person: BaseViewField & { value: StringArrayValue }
        createdTime: DateViewField & { value: DateValue }
        updatedTime: DateViewField & { value: DateValue }
        number: NumberViewField & { value: NumberValue }
        select: SelectViewField & { value: StringArrayValue }
        date: DateViewField & { value: DateValue }
        formula: ViewFieldProtocol & { value: FormulaValue }
        notes: BaseViewField & { value: RichTextValue }
        role: BaseViewField & { value: StringArrayValue }
        department: BaseViewField & { value: StringArrayValue }
        checkbox: CheckboxViewField & { value: BoolValue }
    }
>

export type KanbanColumnsSortItem = {
    value: string
    visible: boolean
}

export type KanbanColumnsSort = Record<FieldId, KanbanColumnsSortItem[]>
